// hooks/useTodoLists.ts
import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  doc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { CalendarEvent, MovieSeriesItem, FunnyWord, TodoLists } from '../types/calendar';

export const useTodoLists = (userId: string, userName: string) => {
  const [todoLists, setTodoLists] = useState<TodoLists>({
    calendarEvents: [],
    moviesSeries: [],
    funnyWords: []
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Cargar eventos de calendario
  useEffect(() => {
    const q = query(collection(db, 'calendarEvents'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const events: CalendarEvent[] = [];
      snapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() } as CalendarEvent);
      });
      setTodoLists(prev => ({ ...prev, calendarEvents: events }));
    });
    return () => unsubscribe();
  }, []);

  // Cargar películas y series
  useEffect(() => {
    const q = query(collection(db, 'moviesSeries'), orderBy('addedAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: MovieSeriesItem[] = [];
      snapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() } as MovieSeriesItem);
      });
      setTodoLists(prev => ({ ...prev, moviesSeries: items }));
    });
    return () => unsubscribe();
  }, []);

  // Cargar palabras graciosas
  useEffect(() => {
    const q = query(collection(db, 'funnyWords'), orderBy('addedAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const words: FunnyWord[] = [];
      snapshot.forEach((doc) => {
        words.push({ id: doc.id, ...doc.data() } as FunnyWord);
      });
      setTodoLists(prev => ({ ...prev, funnyWords: words }));
    });
    return () => unsubscribe();
  }, []);

  // Funciones para eventos de calendario
  const addCalendarEvent = useCallback(async (eventData: Omit<CalendarEvent, 'id' | 'createdBy' | 'createdAt'>) => {
    setIsLoading(true);
    try {
      await addDoc(collection(db, 'calendarEvents'), {
        ...eventData,
        createdBy: userName,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding calendar event:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userName]);

  const updateCalendarEvent = useCallback(async (id: string, updates: Partial<CalendarEvent>) => {
    try {
      await updateDoc(doc(db, 'calendarEvents', id), updates);
    } catch (error) {
      console.error('Error updating calendar event:', error);
    }
  }, []);

  const deleteCalendarEvent = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'calendarEvents', id));
    } catch (error) {
      console.error('Error deleting calendar event:', error);
    }
  }, []);

  // Funciones para películas y series
  const addMovieSeries = useCallback(async (itemData: Omit<MovieSeriesItem, 'id' | 'addedBy' | 'addedAt'>) => {
    setIsLoading(true);
    try {
      await addDoc(collection(db, 'moviesSeries'), {
        ...itemData,
        addedBy: userName,
        addedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding movie/series:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userName]);

  const updateMovieSeries = useCallback(async (id: string, updates: Partial<MovieSeriesItem>) => {
    try {
      await updateDoc(doc(db, 'moviesSeries', id), updates);
    } catch (error) {
      console.error('Error updating movie/series:', error);
    }
  }, []);

  const deleteMovieSeries = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'moviesSeries', id));
    } catch (error) {
      console.error('Error deleting movie/series:', error);
    }
  }, []);

  // Funciones para palabras graciosas
  const addFunnyWord = useCallback(async (wordData: Omit<FunnyWord, 'id' | 'addedBy' | 'addedAt'>) => {
    setIsLoading(true);
    try {
      await addDoc(collection(db, 'funnyWords'), {
        ...wordData,
        addedBy: userName,
        addedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding funny word:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userName]);

  const updateFunnyWord = useCallback(async (id: string, updates: Partial<FunnyWord>) => {
    try {
      await updateDoc(doc(db, 'funnyWords', id), updates);
    } catch (error) {
      console.error('Error updating funny word:', error);
    }
  }, []);

  const deleteFunnyWord = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'funnyWords', id));
    } catch (error) {
      console.error('Error deleting funny word:', error);
    }
  }, []);

  // Funciones utilitarias
  const getUpcomingEvents = useCallback((days: number = 7) => {
    const now = new Date();
    const futureDate = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000));
    
    return todoLists.calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= now && eventDate <= futureDate && !event.completed;
    });
  }, [todoLists.calendarEvents]);

  const getWatchListStats = useCallback(() => {
    const total = todoLists.moviesSeries.length;
    const watched = todoLists.moviesSeries.filter(item => item.watched).length;
    const movies = todoLists.moviesSeries.filter(item => item.type === 'movie').length;
    const series = todoLists.moviesSeries.filter(item => item.type === 'series').length;
    
    return { total, watched, pending: total - watched, movies, series };
  }, [todoLists.moviesSeries]);

  return {
    todoLists,
    isLoading,
    // Calendar functions
    addCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
    // Movie/Series functions
    addMovieSeries,
    updateMovieSeries,
    deleteMovieSeries,
    // Funny words functions
    addFunnyWord,
    updateFunnyWord,
    deleteFunnyWord,
    // Utility functions
    getUpcomingEvents,
    getWatchListStats
  };
};